import React from 'react';
import { connect } from 'react-redux';
import { Modal, Form } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { firstError, firstResponseError, pruneServerErrors } from '@/utils/errors';
import findResult, { findMatch } from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import DialogWrapper from '@/components/dialogs/DialogWrapper';
import { newDatasourceSettings } from '../state/state';
import DatasourceForm, { datasetDatasourceSettingsComponents } from '../components/DatasourceForm';
import DatasourceFormActions from '../components/DatasourceFormActions';
import {
  hideDatasourceSettings,
  saveDatasourceSettings,
  testDatasourceSettings,
  updateDatasourceSettings,
} from '../actions';
import hasDatasourceChanged, { supportedDatasourceTypes } from '../helper';

const DatasourceDialog = ({
  hide,
  isDirty,
  readErrors,
  readStatus,
  settings,
  saveSettings,
  supportedTypes,
  testSettings,
  theme,
  updateSettings,
}) => {
  const intl = useIntl();
  const handleDismiss = () => {
    updateSettings({
      saveErrors: pruneServerErrors(settings.saveErrors),
      testErrors: pruneServerErrors(settings.testErrors),
    });
  };

  const submitForm = () => {
    const { validator } = datasetDatasourceSettingsComponents[settings.type];
    const validationErrors = [];
    if (validator) { validator(settings, validationErrors); }
    updateSettings(settings.category, settings.code, { saveErrors: validationErrors });

    if (validationErrors.length === 0) {
      saveSettings(settings.category, settings.code);
    }
  };

  if (!settings.open) { return null; }

  return (
    <DialogWrapper
      className="aridhia-dataset-datasource-settings tinywizard"
      error={firstResponseError([...readErrors, ...settings.saveErrors, ...settings.testErrors])}
      icon="server"
      loading={readStatus === states.PENDING}
      onClose={() => hide(settings.category, settings.code)}
      onDismiss={handleDismiss}
      size="small"
      theme={theme}
      title={intl.formatMessage({ id: 'settings.datasource.edit.title' }, { name: settings.title })}
    >
      <Modal.Content scrolling>
        <Form autoComplete="off" onSubmit={submitForm} error={firstError(settings.saveErrors) !== null}>
          <DatasourceForm
            autoFocus
            readStatus={readStatus}
            settings={settings}
            supportedTypes={supportedTypes}
            updateSettings={(properties) => updateSettings(settings.category, settings.code, properties)}
          />
        </Form>
      </Modal.Content>
      <DatasourceFormActions
        disabled={!findMatch(supportedTypes, settings.type)?.enabled}
        hide={hide}
        isDirty={isDirty}
        settings={settings}
        submitForm={submitForm}
        testSettings={() => testSettings(settings.category, settings.code)}
      />
    </DialogWrapper>
  );
};

const mapStateToProps = ({
  datasetDatasource: { activeDatasourceSetting, settings, datasources },
}) => {
  const { category, code } = activeDatasourceSetting;
  const item = findResult(settings, category, code) || newDatasourceSettings({ code });
  const supportedTypes = supportedDatasourceTypes(datasources.all.results, item);

  return {
    isDirty: hasDatasourceChanged(activeDatasourceSetting, item),
    readErrors: item.readErrors,
    readStatus: item.readStatus,
    settings: activeDatasourceSetting,
    supportedTypes,
  };
};
const mapDispatchToProps = {
  hide: hideDatasourceSettings,
  saveSettings: saveDatasourceSettings,
  testSettings: testDatasourceSettings,
  updateSettings: updateDatasourceSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(DatasourceDialog);
